import React, { useEffect, useState } from 'react';
import {
  Box,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { ReactComponent as LogoDark } from '../../../assets/images/clipsal_logo_dark.svg';
import { ReactComponent as Logo } from '../../../assets/images/clipsal_logo.svg';
import { CustomButton } from '../../../common/components/CustomButton';
import { TermsAndConditionsModal } from '../TermsAndConditionsModal';

import 'react-phone-input-2/lib/bootstrap.css';
import '../../../styles/react-phone-input-2.css';

import { Browser } from '@capacitor/browser';
import { Auth } from 'aws-amplify';
import generator from 'generate-password-ts';
import { useSelector } from 'react-redux';
import { ulid } from 'ulid';

import { CORTEX_PRIVACY_POLICY_URL } from 'clipsal-cortex-utils/src/constants/common-constants';

import { NetworkError } from '../../../api/api-helpers';
import { useAppDispatch } from '../../../app/hooks';
import { baseAddressSchema } from '../../../common/components/address/select-address-helpers';
import { SelectAddress } from '../../../common/components/address/SelectAddress';
import { CLIENT_ID, USER_POOL_ID } from '../../../common/constants';
import { AWS_REGION } from '../../../region-type';
import { getPhoneValidationSchema } from '../../../utils/phone-validation';
import { addUserDetails, selectUser } from '../../user/userSlice';

const initialState = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '+61',
  businessName: '',
  address: '',
  city: '',
  state: '',
  postCode: '',
  country: '',
};

type SignupFormData = typeof initialState;

const schema = yup
  .object()
  .shape({
    email: yup.string().email('*Invalid email format').trim().required('*Email is Required'),
    firstName: yup.string().trim().required('*First Name is required'),
    lastName: yup.string().trim().required('*Last Name is required'),
    phone: getPhoneValidationSchema(),
    businessName: yup.string().trim().required('*Business Name is required'),
    address: yup.string().trim().required('*Business Address is required'),
  })
  .concat(baseAddressSchema);

export function SignupForm() {
  const methods = useForm<SignupFormData>({ resolver: yupResolver(schema), defaultValues: initialState });
  const {
    register,
    handleSubmit: handleFormSubmit,
    getValues,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = methods;

  const [isFormFilled, setIsFormFilled] = useState(false);

  // Check if all fields are filled and enable signup button.
  // Using subscription so that we do not rerender whole component on value changes
  useEffect(() => {
    const subscription = watch((values) => {
      const isFormFilled = Object.values(values).every((value) => !!value);
      setIsFormFilled(isFormFilled);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const [isLoading, setIsLoading] = useState(false);

  const { phoneInputClassName, LogoIcon } = useColorModeValue(
    { phoneInputClassName: 'light-mode-phone-input', LogoIcon: Logo },
    {
      phoneInputClassName: 'dark-mode-phone-input dark-mode-phone-input-transparent-bg',
      LogoIcon: LogoDark,
    }
  );

  const {
    isOpen: isTermsAndConditionsModalOpen,
    onOpen: onOpenTermsAndConditionsModal,
    onClose: onCloseTermsAndConditionsModal,
  } = useDisclosure();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const user = useSelector(selectUser);

  useEffect(() => {
    // check if there is signup details in redux
    // this is required in the event where user
    // wants to change email when coming back from otp screen
    reset({
      email: user.user_email,
      firstName: user.user_first_name,
      lastName: user.user_last_name,
      businessName: user.tenant.tenant_name,
      address: user.tenant.address || '',
      city: user.tenant.city || '',
      state: user.tenant.state || '',
      postCode: user.tenant.zipcode || '',
      country: user.tenant.country || '',
      phone: user.user_phone || '+61',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(values: typeof initialState) {
    const { email, phone, firstName, lastName } = values;
    const username = ulid();

    // Revert to SRP for sign up
    await Auth.configure({
      region: AWS_REGION,
      userPoolId: USER_POOL_ID,
      userPoolWebClientId: CLIENT_ID,
      authenticationFlowType: 'USER_SRP_AUTH',
    });

    // generate random password
    const tempPassword = generator.generate({
      length: 12,
      numbers: true,
      symbols: true,
      uppercase: true,
      lowercase: true,
      strict: true,
    });

    setIsLoading(true);
    try {
      await Auth.signUp({
        username,
        password: tempPassword,
        attributes: {
          email: email.toLowerCase(),
        },
        clientMetadata: {
          user_first_name: firstName,
          user_last_name: lastName,
          user_phone: phone,
          tenant_name: values.businessName,
          address: values.address,
          city: values.city,
          country: values.country,
          logo_url: '',
          state: values.state,
          zipcode: values.postCode,
        },
      });

      dispatch(
        addUserDetails({
          ...values,
          user_email: email.toLowerCase(),
          user_first_name: values.firstName,
          user_last_name: values.lastName,
          user_name: username,
          user_phone: phone,
          tempPassword: tempPassword,
          tenant: {
            tenant_id: 0,
            tenant_name: values.businessName,
            address: values.address,
            city: values.city,
            country: values.country,
            logo_url: '',
            state: values.state,
            zipcode: values.postCode,
          },
        })
      );
      setIsLoading(false);
      navigate('/signup/account-activation-info');
    } catch (error) {
      const errorToastConfig = {
        title: 'Something went wrong!',
        description: 'Please contact us if this issue persists.',
      };
      if ((error as NetworkError)?.message.includes('Email or username already in use')) {
        errorToastConfig.title = 'Email already in use!';
        errorToastConfig.description = 'Please use a different email.';
      }
      toast({
        ...errorToastConfig,
        status: 'error',
        isClosable: true,
      });
      setIsLoading(false);
    }
  }

  return (
    <FormProvider {...methods}>
      <Center flexDirection={'column'} textAlign={'center'} my={5}>
        <Box cursor={'pointer'} my={5} w={'60%'}>
          <LogoIcon />
        </Box>
        <Text fontSize={'md'} mt={4}>
          Let’s get started!
        </Text>
      </Center>

      <Box onSubmit={handleFormSubmit(handleSubmit)} as={'form'} data-testid="signup-form">
        <FormControl my={3} isInvalid={!!errors.email}>
          <FormLabel>Email</FormLabel>
          <Input {...register('email')} type="email" placeholder="Enter Email" data-testid="email" />
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
        </FormControl>

        <Box my={3}>
          <FormLabel>Full Name</FormLabel>
          <Flex>
            <FormControl isInvalid={!!errors.firstName} pr={2}>
              <Input {...register('firstName')} placeholder="First" data-testid="first-name" />
              <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.lastName} pl={2}>
              <Input {...register('lastName')} placeholder="Last" data-testid="last-name" />
              <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
            </FormControl>
          </Flex>
        </Box>

        <FormControl my={3} isInvalid={!!errors.phone}>
          <FormLabel>Phone</FormLabel>
          <Box className={phoneInputClassName} data-testid="phone">
            <PhoneInput
              isValid={!errors.phone}
              placeholder="Enter your number"
              country="au"
              autoFormat
              masks={{ au: '.... ... ...' }}
              onlyCountries={['au', 'nz']}
              countryCodeEditable={false}
              enableAreaCodeStretch
              value={getValues().phone}
              onEnterKeyPress={handleFormSubmit(handleSubmit)}
              onChange={(phone) => {
                setValue(`phone`, '+' + phone, {
                  shouldDirty: true,
                  shouldValidate: !!errors.phone,
                });
              }}
            />
          </Box>
          <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
        </FormControl>

        <FormControl mt={4} isInvalid={!!errors.businessName}>
          <FormLabel>Business name</FormLabel>
          <Input {...register('businessName')} placeholder="Enter business name" data-testid="business-name" />
          <FormErrorMessage>{errors.businessName?.message}</FormErrorMessage>
        </FormControl>

        <SelectAddress<SignupFormData>
          testId="business-address"
          inputTitle="Business address"
          containerProps={{ mt: 4 }}
          predictionContainerProps={{ width: '100%' }}
        />

        <Text fontSize={'sm'} my={8}>
          By signing up here, you accept the Clipsal Cortex
          <Box
            mx={1}
            cursor={'pointer'}
            _hover={{ textDecoration: 'underline' }}
            onClick={onOpenTermsAndConditionsModal}
            as={'span'}
            color="customLinkBlue.500"
            data-testid="open-terms-and-conditions-button"
          >
            Terms and Conditions
          </Box>
          and
          <Box
            mx={1}
            cursor={'pointer'}
            _hover={{ textDecoration: 'underline' }}
            data-testid="privacy-policy-link"
            onClick={() => Browser.open({ url: CORTEX_PRIVACY_POLICY_URL })}
            as={'span'}
            color="customLinkBlue.500"
          >
            Privacy Policy
          </Box>
        </Text>

        <CustomButton
          data-testid="signup-button"
          isLoading={isLoading}
          isDisabled={!isFormFilled}
          loadingText={'Signing up...'}
          w="100%"
          rounded={3}
          py={6}
        >
          Signup
        </CustomButton>

        <Center mt={6}>
          <Text fontSize={'sm'}>
            Already have an account?
            <Box
              cursor={'pointer'}
              _hover={{ textDecoration: 'underline' }}
              onClick={() => navigate('/login')}
              as={'span'}
              fontWeight={'bold'}
              color="customLinkBlue.500"
              ml={1}
            >
              Sign in
            </Box>
          </Text>
        </Center>
      </Box>

      {isTermsAndConditionsModalOpen && (
        <TermsAndConditionsModal isOpen={isTermsAndConditionsModalOpen} onClose={onCloseTermsAndConditionsModal} />
      )}
    </FormProvider>
  );
}
